import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Amount, Button, Icon, Sheet } from "@qota/ui";
import { deleteExpense, listExpenses } from "../lib/expenses";
import { queryKeys } from "../lib/query-keys";

/**
 * Il dettaglio di una spesa, con modifica ed eliminazione.
 *
 * Stava dentro la schermata del gruppo, che ora è un cruscotto e non elenca
 * più le spese. Estraendolo, il foglio segue l'elenco dovunque vada invece di
 * restare attaccato a una schermata che non lo apre più.
 */
export function ExpenseSheet({
  groupId,
  expenseId,
  onClose,
}: {
  groupId: string;
  expenseId: string | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: expenses } = useQuery({
    queryKey: queryKeys.expenses(groupId),
    queryFn: () => listExpenses(groupId),
  });

  const selected = expenses?.find((expense) => expense.id === expenseId);

  const removal = useMutation({
    mutationFn: (id: string) => deleteExpense(groupId, id),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: queryKeys.expenses(groupId) });
      // Eliminare una spesa cambia i saldi: senza questa, il cruscotto
      // continuerebbe a mostrare i numeri di prima.
      await queryClient.invalidateQueries({ queryKey: queryKeys.balances(groupId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.timeline(groupId) });
    },
  });

  return (
    <Sheet
      open={selected !== undefined}
      title={selected?.description}
      subtitle={selected ? `Pagata da ${selected.payerNames.join(", ")}` : undefined}
      onClose={onClose}
      footer={
        <>
          <Button
            variant="secondary"
            size="lg"
            full
            iconLeft={<Icon name="pencil" size={17} />}
            onClick={() =>
              selected &&
              void navigate({
                to: "/gruppi/$groupId/spese/$expenseId/modifica",
                params: { groupId, expenseId: selected.id },
              })
            }
          >
            Modifica
          </Button>
          <Button
            variant="danger"
            size="lg"
            full
            disabled={removal.isPending}
            iconLeft={<Icon name="trash-2" size={17} />}
            onClick={() => selected && removal.mutate(selected.id)}
          >
            {removal.isPending ? "Elimino…" : "Elimina"}
          </Button>
        </>
      }
    >
      {selected && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ textAlign: "center", padding: "6px 0 10px" }}>
            <Amount cents={selected.amountCents} size={32} tone="neutral" />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ color: "var(--text-secondary)" }}>Divisa fra</span>
            <strong>{selected.participantCount} persone</strong>
          </div>

          {selected.myShareCents !== null && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <span style={{ color: "var(--text-secondary)" }}>La tua quota</span>
              <strong>{(selected.myShareCents / 100).toFixed(2).replace(".", ",")} €</strong>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ color: "var(--text-secondary)" }}>Data</span>
            <strong>
              {new Date(selected.spentAt).toLocaleDateString("it-IT", {
                day: "numeric",
                month: "short",
              })}
            </strong>
          </div>
        </div>
      )}
    </Sheet>
  );
}
